import { t } from "translations"

import List from "@mui/material/List"
import ListItem from "@mui/material/ListItem"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { toLocalDate } from "@nhi/utils"
import { IArticleCard } from "data/contentData/interfaces/mongodb/IArticleCard"

import Icon from "Components/Icon/Icon"
import ContentImage from "Components/Image/ContentImage"
import Link, { ILinkProps } from "Components/Link"

export default function ArchiveCard({
    articles
}: {
    articles: IArticleCard[]
}) {
    return (
        <List>
            {articles.map((article: IArticleCard) => (
                <ListItem
                    disablePadding
                    key={article.contentId}>
                    <StyledListItemButton
                        hasimage={article.image ? "true" : "false"}
                        LinkComponent={Link}
                        href={article.url}>
                        <ContentContainer>
                            <StyledArticleTopic variant="overline">
                                <Icon
                                    name="articleOutlined"
                                    fontSize="inherit"
                                    color="secondary"
                                />
                                {article.template}
                            </StyledArticleTopic>

                            <Typography variant="h3">
                                {article.title}
                            </Typography>

                            <StyledIngress variant="body2">
                                {article.ingress}
                            </StyledIngress>

                            <StyledDateAndTopic
                                variant="subtitle2"
                                component="p">
                                {toLocalDate(
                                    article.latest ?? article.published!,
                                    process.env.NEXT_PUBLIC_LOCALE
                                )}{" "}
                                -{article.topic ?? `${t.ads.defaultAdTitle}`}
                            </StyledDateAndTopic>
                        </ContentContainer>

                        {article.image && (
                            <StyledContentImage image={article.image} />
                        )}
                    </StyledListItemButton>
                </ListItem>
            ))}
        </List>
    )
}

type ListItemProps = ILinkProps & {
    hasimage: string
}

const StyledListItemButton = styled(ListItemButton)<ListItemProps>(
    ({ theme, hasimage }) => ({
        display: "grid",
        gridTemplateColumns: hasimage === "true" ? "3fr 1fr" : "1fr",
        gap: theme.spacing(2),
        "&.MuiListItemButton-root": {
            paddingRight: theme.spacing(2),
            marginRight: theme.spacing(-2),
            paddingLeft: theme.spacing(2),
            marginLeft: theme.spacing(-2)
        }
    })
)

const ContentContainer = styled(ListItemText)(() => ({
    gridColumn: 1
}))

const StyledArticleTopic = styled(Typography)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5)
}))

const StyledIngress = styled(Typography)(() => ({
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    lineClamp: "2",
    WebkitBoxOrient: "vertical"
}))

const StyledDateAndTopic = styled(Typography)<{ component: string }>(
    ({ theme }) => ({
        color: theme.palette.text.secondary
    })
)

const StyledContentImage = styled(ContentImage)(({ theme }) => ({
    marginLeft: "auto",
    width: "96px",
    height: "54px",
    objectFit: "cover",
    [theme.breakpoints.up("sm")]: {
        width: "158px",
        height: "89px"
    }
}))
